import { Box } from '@chakra-ui/react'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Loading } from '@/components/Loading'
import { LoadingFailedAlert } from '@/components/LoadingFailedAlert'
import { Note } from '@/components/Note'
import { Steps } from '@/components/Steps/Steps'
import { WizzardHeader } from '@/components/WizzardHeader/WizzardHeader'
import { Step3 } from '@/containers/steps/property/Step3'
import { useInsuranceParams } from '@/context/InsuranceParamsProvider'
import { QuotesProvider } from '@/context/QuotesContext'
import { SelectedQuoteProvider } from '@/context/SelectedQuoteContext'
import { SidebarProvider } from '@/context/SidebarContext'
import { StepsProvider } from '@/context/StepsContext'
import { WizzardHeaderProvider } from '@/context/WizzardHeaderContext'
import { WithAuth } from '@/hoc/WithAuth'
import { useApp } from '@/hooks/useApp'
import { useGetAdditionalData } from '@/hooks/useWizard'
import { PageWrapper } from '@/layouts/components/PageWrapper'
import type { PageSection } from '@/layouts/components/Sidebar'
import { Sidebar } from '@/layouts/components/Sidebar'
import type { WizardPropertyAdditionalData } from 'BrokerTsLib/src/dtos'
import { GetWizardProductPrefix } from '../vehicle/4'

export const Step3Sections = {
  additionalInfo: 1,
  pojistnik: 2,
  pojisteny: 3,
  address: 4
} as const

export type Step3Sections = typeof Step3Sections[keyof typeof Step3Sections]

const Step3PageComponent = () => {
  const navigate = useNavigate()
  const { product, setProduct } = useApp()
  const [noteText, setNoteText] = useState<string | undefined>(undefined)

  const { insuranceParams, insuranceParamsId: paramsId, isLoadingData, isFetchingData, refetchInsuranceParams, error } = useInsuranceParams()
  const { isLoading, data } = useGetAdditionalData(paramsId ?? '')

  const productPrefix = GetWizardProductPrefix(product!)

  useEffect(() => {
    if (!insuranceParams) return

    if (!insuranceParams.generalParams.selectedOfferId && !isFetchingData && !isLoadingData) {
      navigate(`${productPrefix}/steps/2/${paramsId}`)
      return
    }
  }, [insuranceParams, isFetchingData])

  const propertyAdditionalData = data?.data as WizardPropertyAdditionalData
  const [pageSections, setPageSections] = useState<PageSection[]>([])
  useEffect(() => {
    const initPageSection = []

    if (propertyAdditionalData?.property?.additionalDataRequired)
      initPageSection.push({ id: 'section_' + Step3Sections.additionalInfo, title: `${Step3Sections.additionalInfo}. Doplňující informace` })

    initPageSection.push({ id: 'section_' + Step3Sections.pojistnik, title: `${Step3Sections.pojistnik}. Pojistník` })
    initPageSection.push({ id: 'section_' + Step3Sections.address, title: `${Step3Sections.address}. Korespondenční adresa`, })

    setPageSections(initPageSection)
  }, [propertyAdditionalData])

  const handleCreateNote = () => {
    setNoteText('Nová poznámka')
  }

  useEffect(() => {
    if (insuranceParams?.generalParams?.insuranceCategoryType)
      setProduct(insuranceParams?.generalParams?.insuranceCategoryType)
  }, [insuranceParams?.generalParams?.insuranceCategoryType])

  return (
    <StepsProvider currentStepId={3}>
      <SidebarProvider pageSections={pageSections}>
        <WizzardHeaderProvider>
          <QuotesProvider>
            {(isLoadingData || isLoading) && <Loading large={true} />}
            {!(isLoadingData || isLoading) && error && <LoadingFailedAlert />}
            {(insuranceParams && product == insuranceParams.generalParams.insuranceCategoryType && data?.data) &&
                            <SelectedQuoteProvider>
                              <PageWrapper
                                steps={<Steps />}
                                header={<WizzardHeader onCreateNote={handleCreateNote} />}
                                sidebar={<Sidebar />}
                                contentProps={{
                                  sx: {
                                    pt: { base: 0, md: 0 },
                                  },
                                }}
                              >
                                {noteText && noteText !== '' && (
                                  <Note
                                    date="11.10.2021 14:35"
                                    text={noteText}
                                    onChange={(text) => setNoteText(text)}
                                  />
                                )}

                                <Box sx={{ pt: { base: 0, md: !noteText ? 8 : 0 } }}>
                                  <Step3 data={data.data as WizardPropertyAdditionalData} />
                                </Box>
                              </PageWrapper>
                            </SelectedQuoteProvider>
            }
          </QuotesProvider>
        </WizzardHeaderProvider>
      </SidebarProvider>
    </StepsProvider>
  )
}

export const Step3Page = WithAuth(Step3PageComponent)